/**
 * Browser-side media conversion utilities for the manager dashboard.
 *
 * Managers upload site photos/videos straight from a phone or camera, which are
 * often far larger than needed. The hcp-admin-backend does NO server-side
 * resizing — it stores exactly the bytes it receives (hard cap 10 MB). So we
 * compress in the browser BEFORE upload:
 *
 *   - Images (Canvas API): downscaled + re-encoded to AVIF (or WebP fallback).
 *     Safe to parallelize.
 *   - Videos (FFmpeg WASM): downscaled + re-encoded to MP4 (or WebM). MUST be
 *     serialized — the FFmpeg WASM singleton shares a virtual filesystem with
 *     hardcoded filenames (input/output), so concurrent calls corrupt each
 *     other. Serialization is handled internally via a lock, so callers can
 *     safely do `await Promise.all([convertVideoToMp4(a), convertVideoToMp4(b)])`.
 *
 * All output formats (image/avif, image/webp, video/mp4, video/webm) are
 * accepted by the backend's MEDIA_ALLOWED_MIME_TYPES.
 *
 * Ported from hcp-admin-frontend/src/utils/mediaConverter.ts.
 */
import { FFmpeg } from '@ffmpeg/ffmpeg';

// ─── Types ───────────────────────────────────────────────────────────────────

export type MediaKind = 'image' | 'video';

export interface ValidationResult {
  valid: boolean;
  error: string | null;
}

export interface ImageConversionResult {
  blob: Blob;
  format: string;
  width: number;
  height: number;
  originalSize: number;
  convertedSize: number;
}

export interface VideoConversionResult {
  blob: Blob;
  format: string;
  originalSize: number;
  convertedSize: number;
}

export interface ProcessFileResult {
  blob: Blob;
  fileName: string;
  mimeType: string;
  wasConverted: boolean;
  originalSize: number;
  convertedSize: number;
}

export interface ImageConversionOptions {
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  preserveAspectRatio?: boolean;
}

/**
 * x264 presets, slowest-to-fastest encoding effort. At a fixed CRF the preset
 * trades encode time against file size: faster presets analyse less, so they
 * need more bits for the same quality. Roughly, `fast` → `veryfast` is ~1.5-2x
 * faster for ~10-15% larger output, though it varies a lot with content.
 */
export type X264Preset =
  | 'ultrafast'
  | 'superfast'
  | 'veryfast'
  | 'faster'
  | 'fast'
  | 'medium'
  | 'slow'
  | 'slower'
  | 'veryslow';

export interface VideoConversionOptions {
  crf?: string;
  preset?: X264Preset;
  maxWidth?: number;
  /**
   * Output frame-rate cap. Halves the frames to encode on 60fps sources — which
   * roughly offsets the cost of a higher resolution — and normalises variable
   * frame-rate phone footage to CFR, which some players handle better.
   *
   * Note it also duplicates frames on sub-30fps sources (a 24fps clip gains
   * ~25% more frames for no visual gain). Phones shoot 30 or 60, so that is rare
   * and not worth the filter-expression complexity to avoid.
   */
  fpsCap?: number;
  onProgress?: (progress: { ratio: number; time: number }) => void;
}

export interface BatchOptions extends ImageConversionOptions {
  onProgress?: (completed: number, total: number, result: ImageConversionResult) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

// Accepted INPUT types. Output is always re-encoded to AVIF/WebP (images) or
// MP4/WebM (video), so even formats the backend rejects on input (gif, mov, …)
// are fine here — they get converted to a backend-valid format.
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'image/bmp', 'image/tiff', 'image/avif',
];

export const SUPPORTED_VIDEO_TYPES = [
  'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo',
  'video/x-matroska', 'video/3gpp', 'video/ogg',
];

// Defaults only. Callers pass values from MEDIA_ENCODING (src/config/
// mediaEncoding.ts); these apply when nothing is supplied. Source-size caps
// guard browser/WASM memory — the CONVERTED size is validated separately
// against GET /api/medias/limits at the call site.
export const MAX_IMAGE_SIZE_BYTES = 25 * 1024 * 1024;    // 25 MB source guard
export const MAX_VIDEO_SIZE_BYTES = 75 * 1024 * 1024;    // 75 MB — covers 1080p30/60 within the duration cap; rejects 4K
export const DEFAULT_IMAGE_QUALITY = 0.75;
export const DEFAULT_VIDEO_CRF = '28';
export const DEFAULT_VIDEO_MAX_WIDTH = 1920;             // 720p visibly softens in a large viewer
export const DEFAULT_VIDEO_FPS_CAP = 30;

// Canvas has a maximum area, enforced most strictly by iOS Safari. Exceeding it
// yields a BLANK canvas rather than an error, so an oversized photo would upload
// "successfully" as a black rectangle. Nothing downstream catches that, which is
// why the dimension check is not optional.
export const MAX_IMAGE_PIXELS = 40_000_000;              // 40 MP
export const MAX_IMAGE_EDGE_PX = 8192;

// ─── FFmpeg WASM singleton + serialize-queue ─────────────────────────────────

let _ffmpeg: FFmpeg | null = null;
let _ffmpegLoading: Promise<FFmpeg> | null = null;

// Self-hosted, same-origin core (copied into public/ffmpeg by
// scripts/copy-ffmpeg.js). Served from our own Firebase Hosting origin with a
// 1-year cache so it downloads once and stays cached — critical for the weak
// wifi at many campgrounds, and removes the runtime dependency on unpkg.com.
// UMD build (NOT esm): the umd entry runs as a classic script and self-resolves
// its worker; the esm entry uses dynamic `import()` which breaks when the parent
// script is loaded via a blob: URL. PUBLIC_URL is '' when no homepage is set,
// yielding "/ffmpeg/ffmpeg-core.js".
const FFMPEG_CORE_BASE = `${process.env.PUBLIC_URL || ''}/ffmpeg`;

async function fetchAsBlobURL(url: string, mimeType: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
  const blob = new Blob([await response.arrayBuffer()], { type: mimeType });
  return URL.createObjectURL(blob);
}

async function getFFmpeg(): Promise<FFmpeg> {
  if (_ffmpeg) return _ffmpeg;
  if (_ffmpegLoading) return _ffmpegLoading;

  _ffmpegLoading = (async () => {
    const ffmpeg = new FFmpeg();
    const coreURL = await fetchAsBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.js`, 'text/javascript');
    const wasmURL = await fetchAsBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm');
    await ffmpeg.load({ coreURL, wasmURL });
    _ffmpeg = ffmpeg;
    _ffmpegLoading = null;
    return ffmpeg;
  })();

  return _ffmpegLoading;
}

/**
 * Serialize-queue for video conversions. The FFmpeg WASM singleton has shared
 * mutable state (virtual filesystem with hardcoded filenames), so concurrent
 * calls would corrupt each other. We wrap each video op so it waits for the
 * previous to finish.
 */
let _videoLock: Promise<void> = Promise.resolve();

async function withVideoLock<T>(fn: () => Promise<T>): Promise<T> {
  const previous = _videoLock;
  let release: () => void = () => {};
  _videoLock = new Promise<void>((res) => {
    release = res;
  });
  try {
    await previous;
    return await fn();
  } finally {
    release();
  }
}

// ─── Error / log helpers ─────────────────────────────────────────────────────

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'unknown error';
}

const LOG_TAIL_SIZE = 15;

/**
 * Ring buffer for FFmpeg's `log` event. FFmpeg WASM throws generic errors on
 * failure; its log stream carries the real cause (bad codec, unsupported input,
 * etc.). We keep the last few lines and append them to the thrown error so the
 * UI/console can show *where* a conversion failed. The `handler` reference is
 * stable so it can be passed to both `.on('log', …)` and `.off('log', …)`.
 */
function createLogTail() {
  const lines: string[] = [];
  const handler = ({ message }: { message: string }) => {
    lines.push(message);
    if (lines.length > LOG_TAIL_SIZE) lines.shift();
  };
  const suffix = () => (lines.length ? `\nFFmpeg log:\n${lines.join('\n')}` : '');
  return { handler, suffix };
}

// ─── Image helpers ───────────────────────────────────────────────────────────

let _avifSupported: boolean | null = null;

async function isAvifEncodingSupported(): Promise<boolean> {
  if (_avifSupported !== null) return _avifSupported;
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    canvas.toBlob(
      (blob) => {
        _avifSupported = blob !== null && blob.type === 'image/avif';
        resolve(_avifSupported);
      },
      'image/avif',
      0.5,
    );
  });
}

function loadImageAsCanvas(
  file: File,
  maxWidth: number,
  maxHeight: number,
  preserveAspectRatio: boolean,
): Promise<{ canvas: HTMLCanvasElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      if (preserveAspectRatio && (width > maxWidth || height > maxHeight)) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      } else if (!preserveAspectRatio) {
        width = Math.min(width, maxWidth);
        height = Math.min(height, maxHeight);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get 2D context.'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      resolve({ canvas, width, height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image. The file may be corrupt.'));
    };

    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error(`Browser failed to encode image as ${mimeType}`));
      },
      mimeType,
      quality,
    );
  });
}

function getExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop()! : '';
}

function replaceExtension(name: string, newExt: string): string {
  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.substring(0, dotIndex) : name;
  return `${base}.${newExt}`;
}

// ─── Public: Validation & Detection ──────────────────────────────────────────

export function validateMediaFile(file: File | null | undefined, type: MediaKind): ValidationResult {
  if (!file) return { valid: false, error: 'No file provided.' };

  if (type === 'image') {
    // HEIC/HEIF (the iPhone default) gets its own message: Chrome and Firefox
    // cannot decode it, so allowing it would produce a blank canvas rather than
    // a working upload. Check the filename too — Windows often supplies an
    // empty MIME type for HEIC files copied off a phone.
    if (/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name)) {
      return {
        valid: false,
        error:
          "HEIC isn't supported — on iPhone, Settings → Camera → Formats → " +
          'Most Compatible, or export as JPEG.',
      };
    }
    if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Unsupported image format: ${file.type || 'unknown'}. Supported: JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF.`,
      };
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      return {
        valid: false,
        error: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the ${MAX_IMAGE_SIZE_BYTES / (1024 * 1024)}MB image size limit.`,
      };
    }
  } else if (type === 'video') {
    if (!SUPPORTED_VIDEO_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Unsupported video format: ${file.type || 'unknown'}. Supported: MP4, WebM, MOV, AVI, MKV, 3GP, OGG.`,
      };
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      return {
        valid: false,
        error: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the ${MAX_VIDEO_SIZE_BYTES / (1024 * 1024)}MB video size limit.`,
      };
    }
  } else {
    return { valid: false, error: `Unknown media type: ${type}. Expected "image" or "video".` };
  }

  return { valid: true, error: null };
}

// ─── Metadata probes (needed before conversion) ──────────────────────────────

/** Natural pixel dimensions of an image file. */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read this image. The file may be corrupt or in an unsupported format.'));
    };
    img.src = url;
  });
}

/** Duration (seconds) and frame size of a video file, without decoding it. */
export function getVideoMetadata(file: File): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read this video. The file may be corrupt or in a format this browser cannot open.'));
    };
    video.src = url;
  });
}

export interface SourceLimits {
  maxImageBytes?: number;
  maxVideoBytes?: number;
  maxImagePixels?: number;
  maxImageEdgePx?: number;
  /** From GET /api/medias/limits — a business rule, not a client setting. */
  maxVideoSeconds?: number;
}

/**
 * Validate a SOURCE file before conversion.
 *
 * Async because dimensions and duration require loading metadata. Conversion
 * costs seconds for images and minutes for video, so everything knowable up
 * front is checked here — a rejection after a two-minute encode is a rejection
 * the admin paid for.
 *
 * Size limits here guard browser/WASM memory; they are NOT predictions of the
 * converted size. Output tracks content and duration, not input size, so the
 * converted result must be measured separately after conversion.
 */
export async function validateSourceFile(
  file: File | null | undefined,
  type: MediaKind,
  limits: SourceLimits = {},
): Promise<ValidationResult> {
  const basic = validateMediaFile(file, type);
  if (!basic.valid) return basic;

  const {
    maxImageBytes = MAX_IMAGE_SIZE_BYTES,
    maxVideoBytes = MAX_VIDEO_SIZE_BYTES,
    maxImagePixels = MAX_IMAGE_PIXELS,
    maxImageEdgePx = MAX_IMAGE_EDGE_PX,
    maxVideoSeconds,
  } = limits;

  const f = file as File;
  const mb = (n: number) => (n / (1024 * 1024)).toFixed(1);

  if (type === 'image') {
    if (f.size > maxImageBytes) {
      return { valid: false, error: `Image is ${mb(f.size)}MB. Maximum: ${mb(maxImageBytes)}MB.` };
    }
    let dims: { width: number; height: number };
    try {
      dims = await getImageDimensions(f);
    } catch (e: any) {
      return { valid: false, error: e?.message || 'Could not read this image.' };
    }
    const longestEdge = Math.max(dims.width, dims.height);
    if (longestEdge > maxImageEdgePx || dims.width * dims.height > maxImagePixels) {
      return {
        valid: false,
        error:
          `Image is too large to process (${dims.width}×${dims.height}). ` +
          `Maximum ${maxImageEdgePx}px per side and ${Math.round(maxImagePixels / 1_000_000)} megapixels. ` +
          `Resize it and try again.`,
      };
    }
    return { valid: true, error: null };
  }

  if (f.size > maxVideoBytes) {
    return {
      valid: false,
      error:
        `Video is ${mb(f.size)}MB. Maximum: ${mb(maxVideoBytes)}MB. ` +
        `Recording at 1080p rather than 4K will usually fit.`,
    };
  }
  if (maxVideoSeconds !== undefined) {
    let meta: { duration: number };
    try {
      meta = await getVideoMetadata(f);
    } catch (e: any) {
      return { valid: false, error: e?.message || 'Could not read this video.' };
    }
    // Fail CLOSED when the duration can't be read. Some MP4s report Infinity or
    // NaN until fully buffered, and treating that as "fine" silently skips the
    // limit entirely — a ten-minute clip would pass, then blow the converted
    // size cap after the admin waited through a long encode.
    if (!Number.isFinite(meta.duration) || meta.duration <= 0) {
      return {
        valid: false,
        error:
          "Couldn't read this video's length, so it can't be checked against the " +
          `${maxVideoSeconds}s limit. Try re-exporting it as MP4 (H.264).`,
      };
    }
    if (meta.duration > maxVideoSeconds) {
      return {
        valid: false,
        error: `Video is ${Math.round(meta.duration)}s. Maximum: ${maxVideoSeconds}s.`,
      };
    }
  }
  return { valid: true, error: null };
}

export async function getSupportedOutputFormat(): Promise<string> {
  const avif = await isAvifEncodingSupported();
  return avif ? 'image/avif' : 'image/webp';
}

export function isVideoConversionSupported(): boolean {
  return typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function';
}

// ─── Public: Image Conversion ────────────────────────────────────────────────

export async function convertImageToAvif(
  file: File,
  options: ImageConversionOptions = {},
): Promise<ImageConversionResult> {
  const {
    quality = DEFAULT_IMAGE_QUALITY,
    maxWidth = 1920,
    maxHeight = 1920,
    preserveAspectRatio = true,
  } = options;

  const validation = validateMediaFile(file, 'image');
  if (!validation.valid) throw new Error(validation.error!);

  const { canvas, width, height } = await loadImageAsCanvas(file, maxWidth, maxHeight, preserveAspectRatio);

  let blob: Blob;
  let format: string;
  const avifSupported = await isAvifEncodingSupported();
  if (avifSupported) {
    blob = await canvasToBlob(canvas, 'image/avif', quality);
    format = 'image/avif';
  } else {
    console.info('[mediaConverter] AVIF encoding not supported by this browser, falling back to WebP.');
    blob = await canvasToBlob(canvas, 'image/webp', quality);
    format = 'image/webp';
  }

  return {
    blob,
    format,
    width,
    height,
    originalSize: file.size,
    convertedSize: blob.size,
  };
}

export async function convertImageBatch(
  files: File[],
  options: BatchOptions = {},
): Promise<ImageConversionResult[]> {
  if (!files || files.length === 0) return [];
  const { onProgress, ...imageOptions } = options;
  const results: ImageConversionResult[] = [];
  for (let i = 0; i < files.length; i++) {
    const result = await convertImageToAvif(files[i], imageOptions);
    results.push(result);
    if (onProgress) onProgress(i + 1, files.length, result);
  }
  return results;
}

// ─── Public: Video Conversion (serialized via internal lock) ─────────────────

export async function convertVideoToMp4(
  file: File,
  options: VideoConversionOptions = {},
): Promise<VideoConversionResult> {
  const validation = validateMediaFile(file, 'video');
  if (!validation.valid) throw new Error(validation.error!);
  if (!isVideoConversionSupported()) {
    throw new Error('Video conversion is not supported in this browser (WebAssembly required).');
  }

  return withVideoLock(async () => {
    const {
      crf = DEFAULT_VIDEO_CRF,
      preset = 'fast',
      maxWidth = DEFAULT_VIDEO_MAX_WIDTH,
      fpsCap = DEFAULT_VIDEO_FPS_CAP,
      onProgress,
    } = options;
    const ffmpeg = await getFFmpeg();

    // Hoist progressHandler so we can pass it to .off() — FFmpeg's off()
    // requires the same handler reference we registered with .on(), otherwise
    // the listener leaks across calls.
    const progressHandler = onProgress
      ? ({ progress, time }: { progress: number; time: number }) => {
          onProgress({ ratio: progress, time });
        }
      : null;
    if (progressHandler) ffmpeg.on('progress', progressHandler);

    // Capture FFmpeg's log output so a failed exec can report *what* went wrong.
    const logTail = createLogTail();
    ffmpeg.on('log', logTail.handler);

    const inputExt = getExtension(file.name) || 'mp4';
    const inputName = `input.${inputExt}`;
    const outputName = 'output.mp4';

    try {
      const fileData = new Uint8Array(await file.arrayBuffer());
      await ffmpeg.writeFile(inputName, fileData);

      // min() so a source smaller than the ceiling is never upscaled — that
      // would cost bytes and add no detail. -2 computes the height preserving
      // aspect ratio while keeping it even, which H.264 requires.
      const scaleFilter = `scale='min(${maxWidth},iw)':-2`;
      await ffmpeg.exec([
        '-i', inputName,
        '-vf', scaleFilter,
        // MUST stay after -i: before it, -r reinterprets the INPUT timestamps
        // instead of setting the output frame rate.
        '-r', String(fpsCap),
        '-c:v', 'libx264',
        '-crf', crf,
        '-preset', preset,
        '-c:a', 'aac',
        '-b:a', '128k',
        '-movflags', '+faststart',
        outputName,
      ]);

      const outputData = await ffmpeg.readFile(outputName) as Uint8Array;
      const blob = new Blob([outputData.buffer as ArrayBuffer], { type: 'video/mp4' });

      return {
        blob,
        format: 'video/mp4',
        originalSize: file.size,
        convertedSize: blob.size,
      };
    } catch (err) {
      throw new Error(`Video conversion failed: ${errMessage(err)}${logTail.suffix()}`);
    } finally {
      await ffmpeg.deleteFile(inputName).catch(() => {});
      await ffmpeg.deleteFile(outputName).catch(() => {});
      if (progressHandler) ffmpeg.off('progress', progressHandler);
      ffmpeg.off('log', logTail.handler);
    }
  });
}

export async function convertVideoToWebm(
  file: File,
  options: VideoConversionOptions = {},
): Promise<VideoConversionResult> {
  const validation = validateMediaFile(file, 'video');
  if (!validation.valid) throw new Error(validation.error!);
  if (!isVideoConversionSupported()) {
    throw new Error('Video conversion is not supported in this browser (WebAssembly required).');
  }

  return withVideoLock(async () => {
    const { crf = '30', maxWidth = 1280, onProgress } = options;
    const ffmpeg = await getFFmpeg();

    // See note in convertVideoToMp4 — handler ref required for proper .off().
    const progressHandler = onProgress
      ? ({ progress, time }: { progress: number; time: number }) => {
          onProgress({ ratio: progress, time });
        }
      : null;
    if (progressHandler) ffmpeg.on('progress', progressHandler);

    const logTail = createLogTail();
    ffmpeg.on('log', logTail.handler);

    const inputExt = getExtension(file.name) || 'mp4';
    const inputName = `input.${inputExt}`;
    const outputName = 'output.webm';

    try {
      const fileData = new Uint8Array(await file.arrayBuffer());
      await ffmpeg.writeFile(inputName, fileData);

      const scaleFilter = `scale='min(${maxWidth},iw)':-2`;
      await ffmpeg.exec([
        '-i', inputName,
        '-vf', scaleFilter,
        '-c:v', 'libvpx-vp9',
        '-crf', crf,
        '-b:v', '0',
        '-c:a', 'libopus',
        '-b:a', '128k',
        outputName,
      ]);

      const outputData = await ffmpeg.readFile(outputName) as Uint8Array;
      const blob = new Blob([outputData.buffer as ArrayBuffer], { type: 'video/webm' });

      return {
        blob,
        format: 'video/webm',
        originalSize: file.size,
        convertedSize: blob.size,
      };
    } catch (err) {
      throw new Error(`Video conversion failed: ${errMessage(err)}${logTail.suffix()}`);
    } finally {
      await ffmpeg.deleteFile(inputName).catch(() => {});
      await ffmpeg.deleteFile(outputName).catch(() => {});
      if (progressHandler) ffmpeg.off('progress', progressHandler);
      ffmpeg.off('log', logTail.handler);
    }
  });
}

// ─── Public: High-Level Convenience ──────────────────────────────────────────

export interface ProcessFileOptions {
  imageQuality?: number;
  imageMaxWidth?: number;
  imageMaxHeight?: number;
  videoFormat?: 'mp4' | 'webm';
  videoMaxWidth?: number;
  videoCrf?: string;
  videoPreset?: X264Preset;
  videoFpsCap?: number;
  onProgress?: (progress: { ratio: number; time: number }) => void;
}

/**
 * Detect file kind and convert to a server-friendly format (AVIF/WebP for images,
 * MP4/WebM for videos). Unsupported types are passed through unchanged.
 *
 * Safe to call concurrently — video calls are serialized internally.
 */
export async function processFileForUpload(
  file: File,
  options: ProcessFileOptions = {},
): Promise<ProcessFileResult> {
  if (!file) throw new Error('No file provided.');

  const {
    imageQuality = DEFAULT_IMAGE_QUALITY,
    imageMaxWidth = 1920,
    imageMaxHeight = 1920,
    videoFormat = 'mp4',
    videoMaxWidth = DEFAULT_VIDEO_MAX_WIDTH,
    videoCrf = DEFAULT_VIDEO_CRF,
    videoPreset = 'fast',
    videoFpsCap = DEFAULT_VIDEO_FPS_CAP,
    onProgress,
  } = options;

  const isImage = SUPPORTED_IMAGE_TYPES.includes(file.type);
  const isVideo = SUPPORTED_VIDEO_TYPES.includes(file.type);

  if (isImage) {
    const result = await convertImageToAvif(file, {
      quality: imageQuality,
      maxWidth: imageMaxWidth,
      maxHeight: imageMaxHeight,
    });
    const ext = result.format === 'image/avif' ? 'avif' : 'webp';
    return {
      blob: result.blob,
      fileName: replaceExtension(file.name, ext),
      mimeType: result.format,
      wasConverted: true,
      originalSize: result.originalSize,
      convertedSize: result.convertedSize,
    };
  }

  if (isVideo) {
    const convertFn = videoFormat === 'webm' ? convertVideoToWebm : convertVideoToMp4;
    const result = await convertFn(file, {
      maxWidth: videoMaxWidth,
      crf: videoCrf,
      preset: videoPreset,
      fpsCap: videoFpsCap,
      onProgress,
    });
    const ext = videoFormat === 'webm' ? 'webm' : 'mp4';
    return {
      blob: result.blob,
      fileName: replaceExtension(file.name, ext),
      mimeType: result.format,
      wasConverted: true,
      originalSize: result.originalSize,
      convertedSize: result.convertedSize,
    };
  }

  // Unsupported type — pass through unchanged
  console.info(`[mediaConverter] Unsupported file type "${file.type}", passing through without conversion.`);
  return {
    blob: file,
    fileName: file.name,
    mimeType: file.type,
    wasConverted: false,
    originalSize: file.size,
    convertedSize: file.size,
  };
}
